import React from "react"
import { graphql, Link } from "gatsby"

import Layout from "../../components/Layout"
import { getOffer, isGated } from "../../data/offers"

import "../../styles/servicePage.scss"

/**
 * /dynamics365/financeOperations/ — the Dynamics 365 managed-services practice.
 *
 * Rebuilt to the /services/aisolution/ template. The page it replaces was a
 * hero, a six-card "Why Businesses Choose Visiomate" grid, a feature strip, the
 * benefits carousel, a simplified-steps block, the ten FAQs inside an antd
 * Collapse, a dark CTA banner and the sitewide client-stories strip.
 *
 * INDEXED. Frozen and byte-identical to git HEAD: the <h1>, the <title>, the
 * meta description, the canonical and the URL. The h1 came from
 * OdooTopSection's headerData.title and is now inline.
 *
 * ONE h1, down from four.
 *
 * ── EIGHT SECTIONS, NOT NINE — no PROOF, for the same reason as
 * /services/staffaugmentation/ and verified the same way. The brief asked which
 * case studies belong to this discipline. Two routes carry Dynamics in their
 * title and NEITHER is a client case study:
 *
 *   /caseStudies/products/mD365BulkStockDistribution/  a product overview of the
 *     D365 Distribution Utility. No client named, no metrics.
 *   /caseStudies/products/micD365XstakIntegration/  titled "Microsoft Dynamics
 *     365 Xstak Integration", but its body was about Visiopack, an Odoo-based
 *     product. RETIRED 5 September 2026 — deleted and now 410 Gone. That
 *     title/content mismatch is why.
 *
 * Every other case study on the site is Odoo or web work. So there is nothing
 * honest to put in a proof section, and `sp--noproof` keeps the grounds
 * alternating across the gap.
 *
 * ── ONE LINK TO mD365BulkStockDistribution, IN §8 AND NOT AS PROOF. That page
 * had 47 inbound links and every one of them was the footer — zero from any
 * page body (INTERNAL-LINK-AUDIT). It is the only other Dynamics route on the
 * site and it genuinely covers bulk distribution on D365, so a contextual
 * mention belongs here. It is deliberately NOT wired into a proof block: the
 * page names no client and reports no metric, which is the same reason this
 * page has no proof section at all. The link says what the page is; it does
 * not claim it is evidence.
 *
 * ── THE TEN FAQs ARE KEPT, rewritten to second person, and they are now IN THE
 * DOM. The component they came from wrapped them in an antd Collapse, which
 * renders no answer text until a panel is opened — invisible to a crawler, and
 * the reason my first term count on this page reported sixteen technology terms
 * missing that had been in the copy all along.
 *
 * ONE DELIBERATE EDIT TO THEIR SUBSTANCE. The onboarding answer said
 * "Onboarding typically takes two to four weeks depending on environment
 * complexity"; a week figure is banned on a service page, so it now turns on
 * environment complexity without the number. The SLA response tiers are KEPT as
 * they were — an hour, four business hours, a business day, two to three
 * business days. Those are response commitments on a running service rather than
 * a delivery timeline, and they are the substance of what a managed service is;
 * stripping them would gut the page. Reported rather than decided quietly.
 *
 * The benefits carousel is gone from this page. After the Odoo page moved to
 * cards, this was the last consumer of .d365-carousel-*, .d365-slide and
 * .d365-arrow — those rules in services.scss and odooServices.scss are now
 * unreferenced but left untouched.
 */

const OFFER_SLUGS = [
  "erp-rescue-sprint",
  "platform-modernization-assessment",
  "ops-and-erp-fit-sprint",
  // Added last, deliberately. The three above are diagnostics that answer a
  // question; this one is the standing arrangement the h1 promises, and it is
  // the first non-diagnostic offer this page has ever listed.
  "dynamics-365-support",
]

// Resolved from the catalogue so a price, a timeline or a slug can never drift
// out of sync with the offer page it points at. All three are diagnostics, and
// the only three offers on the site whose prices are not gated.
const PRACTICE_OFFERS = OFFER_SLUGS.map(getOffer).filter(Boolean)

/* 2. The three audiences the brief named — businesses whose rollout stalled,
   teams paying for modules nobody opens, and finance operations running on
   exports into Excel — two problems each. */
const PROBLEMS = [
  "The rollout stopped somewhere short of live, and nobody can tell you what is left to do or what it would cost to finish.",
  "You are paying a licence for modules nobody has opened since go-live.",
  "Your month-end runs on exports into Excel, and the spreadsheet is the version people actually trust.",
  "A batch job failed overnight and you found out from a user rather than from a monitor.",
  "Every Microsoft update is a week of held breath, because nobody is certain which of your customisations it will break.",
  "The partner who built it has moved on, and the only documentation is in the heads of people who no longer work here.",
]

/* 4. What the managed service actually covers. Drawn from the scope the FAQs
   already state, so no new commitment is introduced here. */
const CAPABILITIES = [
  {
    title: "Monitoring that finds it before your users do",
    body:
      "System performance, batch jobs, integration uptime and patch status watched continuously rather than checked when somebody complains. A failed overnight job raises an alert on this side, not a ticket on yours.",
    stack: ["Monitoring", "Batch jobs", "Integration uptime", "Patch status"],
  },
  {
    title: "Incidents worked to a defined severity and a defined clock",
    body:
      "Triage, root cause analysis, resolution and a prevention plan, against response times agreed in your service level agreement during onboarding rather than negotiated during an outage. Severity is defined in writing, so nobody argues about what critical means at two in the morning.",
    stack: ["SLA", "Incident management", "Root cause", "Severity tiers"],
  },
  {
    title: "Updates tested against your customisations first",
    body:
      "Every release wave and routine patch gets an impact assessment against your environment, deployment to a sandbox, regression testing and your sign-off before anything reaches production — with a documented rollback if it still goes wrong.",
    stack: ["Release waves", "Sandbox", "Regression testing", "Rollback"],
  },
  {
    title: "Consultancy hours for the changes you actually want",
    body:
      "A defined allocation each period for configuration changes, workflow adjustments, new reports and the enhancements that never make it onto a project plan. Break-fix is the floor, not the ceiling — the hours are yours to spend on improvement.",
    stack: ["Consultancy hours", "Configuration", "Workflow", "X++"],
  },
  {
    title: "The integrations nobody else owns",
    body:
      "The connections between Dynamics 365 and everything else you run — the interfaces, the scheduled syncs, the third-party systems — monitored and maintained as part of the service rather than treated as somebody else's problem when they fail.",
    stack: ["API", "Integrations", "Third-party systems", "Azure"],
  },
  {
    title: "A monthly account of what you got",
    body:
      "Governance reviews with a service report you can hold up: incidents raised and resolved, time to resolution, performance against the targets you agreed, hours used and what they bought. Where a target was missed, the corrective action is written down.",
    stack: ["Governance", "Service reports", "Ticketing", "Baseline"],
  },
]

/* 5. Named phases, no numbers and no dates. Reworks the onboarding sequence the
   FAQs describe — assessment, service model, tooling, baseline — into phases. */
const PHASES = [
  {
    phase: "Before anything is agreed",
    title: "An assessment of what you actually have",
    body:
      "A full audit of your environment: the customisations, the configuration, the integrations, the code quality and the issues already outstanding. It does not matter who built it — there is no requirement that the original implementation came from here.",
  },
  {
    phase: "Agreeing the service",
    title: "Response times, severities and who to call",
    body:
      "The service level agreement is written before cover starts: what counts as critical, what response each severity gets, how something is escalated and through which channel. Your internal team keeps the business decisions and the user administration; the platform expertise sits here.",
  },
  {
    phase: "Setting up",
    title: "Into your tools, not ours",
    body:
      "Access provisioning, monitoring configuration, and integration with the ticketing and collaboration tools your team already uses — so the engagement joins your process rather than adding a second queue to check.",
  },
  {
    phase: "The starting point",
    title: "A baseline you can measure against",
    body:
      "A complete record of the environment as it stands at the start, in documentation you keep. Every change after that is measured against it, which is what makes an improvement claim checkable rather than a matter of opinion.",
  },
  {
    phase: "For as long as it runs",
    title: "Reviewed, adjusted, and yours to leave",
    body:
      "Governance reviews look at system health, what was improved and what is next, and at whether the shape of the cover still fits. Your environment, your documentation and your baseline are yours — a handover to another partner is a process, not a hostage negotiation.",
  },
]

/* 8. THE TEN FAQs, KEPT. Rewritten from third person about us — "we monitor
   proactively", "Visiomate takes responsibility" — to second person about the
   reader. Substance unchanged except the onboarding duration, which carried a
   week figure. See the file header. */
const FAQS = [
  {
    q: "What are Dynamics 365 managed services?",
    a: "It is a standing arrangement in which responsibility for the health, performance and continuous improvement of your Dynamics 365 environment sits outside your team. Rather than waiting for you to report a problem, the environment is monitored, incidents are resolved, updates are managed and the platform is optimised — so your people spend their time on the business rather than on platform upkeep.",
  },
  {
    q: "How is this different from Microsoft's own support or a standard IT helpdesk?",
    a: "Microsoft's support covers licensing, product bugs and platform-level issues. It does not cover your environment, your customisations, your integrations or your business processes. A standard helpdesk reacts to tickets. This differs on both counts:",
    bullets: [
      "Your specific Dynamics 365 configuration is known, not just the product in general.",
      "Issues are found and fixed before your users report them.",
      "Customisations, integrations, workflows and business-process changes are in scope, not only the base platform.",
      "You get consultancy hours for improvements, not only break-fix responses.",
      "You are dealing with a specialist extension of your team rather than a third-party ticket queue.",
    ],
  },
  {
    q: "What is included in the scope — and what is not?",
    a: "Included in every engagement:",
    bullets: [
      "Proactive monitoring: system performance, batch jobs, integration uptime and patch status.",
      "Incident management: triage, root cause analysis, resolution and prevention planning.",
      "Patch and update management: impact assessment, regression testing and controlled deployment.",
      "Consultancy hours: configuration changes, enhancements and workflow adjustments.",
      "Integration support: monitoring and maintenance of third-party system connections.",
      "Monthly governance reviews and transparent service quality reporting.",
    ],
    after:
      "Not included by default: new module implementations, major re-implementations, or large net-new development projects. Those are scoped and priced separately.",
  },
  {
    q: "What are your response times, and is cover available around the clock?",
    a: "Response times are set by severity tier and agreed in your service level agreement during onboarding. Typical commitments:",
    bullets: [
      "Critical, meaning the system is down and the business has halted: response within one hour, around the clock.",
      "High, meaning a major function is impaired: response within four business hours.",
      "Medium, meaning partial impact with a workaround available: response within one business day.",
      "Low, meaning a minor issue or an enhancement request: response within two to three business days.",
    ],
    after:
      "Around-the-clock cover applies to critical and high-severity incidents. Extended cover for the lower tiers can be agreed if your operations need it.",
  },
  {
    q: "Can you manage an environment a different partner implemented?",
    a: "Yes, and it is a common starting point. Onboarding begins with a full environment assessment — customisations, configuration, integrations, code quality and outstanding issues — so the system is understood before any cover begins. There is no requirement that the original implementation came from here.",
  },
  {
    q: "How are our customisations protected when Microsoft releases an update?",
    a: "Every update, whether a major release wave or a routine patch, is assessed against your environment before it is applied:",
    bullets: [
      "Impact assessment: how the update affects your customisations, integrations and active workflows.",
      "Sandbox testing: changes go to a non-production environment and are regression-tested first.",
      "Sign-off: your team validates the behaviour in test before anything reaches production.",
      "Controlled release: production deployment follows a documented plan with a confirmed rollback procedure.",
      "Post-deployment monitoring: performance is watched in real time after every release, so unexpected behaviour surfaces early.",
    ],
  },
  {
    q: "What if we already have an internal IT team?",
    a: "This complements an internal team rather than replacing one, and most organisations using it have their own IT staff. The difference is that Dynamics 365 needs deep specialist knowledge that a generalist team cannot always sustain alongside everything else on their list:",
    bullets: [
      "Your team keeps control of business decisions, user management and day-to-day operations.",
      "The Dynamics 365 specifics sit here: platform monitoring, update management, integrations and X++ customisations.",
      "Work happens inside your existing ticketing and communication tools, so nothing about your process has to change.",
      "Escalation paths are agreed upfront, so your team always knows when and how to bring someone in.",
    ],
  },
  {
    q: "What does onboarding involve, and how quickly can we start?",
    a: "How long it takes depends on how complex your environment is, and it is scoped before it starts rather than estimated at you. It covers:",
    bullets: [
      "Environment assessment: a full audit of your instance, customisations, integrations and known issues.",
      "Service model agreement: response times, severity classifications, escalation paths and communication channels.",
      "Tooling setup: access provisioning, monitoring configuration, and integration with your ticketing or collaboration tools.",
      "Baseline documentation: a complete record of the environment at the start, used to measure every change after it.",
    ],
    after:
      "No existing managed services partner is required to begin, and no Microsoft cloud solution provider — CSP — relationship either.",
  },
  {
    q: "How do we know the service is actually delivering value?",
    a: "Because you get a written account of it every period rather than a reassurance. Each month you receive:",
    bullets: [
      "A service report: every incident raised and resolved, time to resolution, and performance against the targets agreed.",
      "A service review: system health, what was improved, and what is coming next.",
      "A log of consultancy hours used and what they produced.",
      "Forward-looking recommendations — what is planned next and why.",
    ],
    after:
      "Where a target is missed, the corrective action is agreed in writing at the next review. You keep a documented record of what was promised against what was delivered.",
  },
  {
    q: "Is there a minimum term, and can the service scale up or down?",
    a: "Engagements are usually structured on an initial twelve-month term, because the parts that pay for themselves — the accumulated knowledge of your environment and the proactive improvements — take time to compound. After that it continues on a rolling basis:",
    bullets: [
      "Consultancy hours can be adjusted up or down at the start of each period, based on what you have planned.",
      "Additional modules, environments or integrations can be added to scope at any time.",
      "If your needs change significantly mid-term, the engagement is adjusted rather than held to a scope that no longer fits.",
    ],
  },
]

const FinanceOperations = ({ data }) => (
  <Layout
    className="services-page-container"
    data={data}
    // FROZEN — byte-identical to git HEAD. Do not touch.
    pageTitle={
      "Dynamics 365 Managed Services | D365 Support & Optimisation | Visiomate"
    }
    descrip={
      "Visiomate delivers expert Microsoft Dynamics 365 managed services — proactive monitoring, SLA-backed support, patch management, and continuous F&O optimisation. Keep your D365 environment healthy and your business moving."
    }
  >
    {/* sp--noproof: neither Dynamics-titled case study names a client or carries
        a metric, so there is no section 7. See the file header. */}
    <article className="sp sp--noproof">
      {/* ── 1. HERO — FULL WIDTH, no split, no rail ─────────────────────────── */}
      <section className="sp-hero">
        <div className="sp-hero__inner">
          <span className="sp-tag">Dynamics 365 practice</span>
          {/* FROZEN h1, byte-identical to the OdooTopSection title it replaces.
              The only h1 on the page. */}
          <h1 className="sp-hero__h1">
            Keep Your Dynamics 365 Running at Its Best &mdash; 24/7 Managed
            Services
          </h1>
          <p className="sp-hero__lede">
            You have already bought the platform, and the question now is whether
            it keeps earning. What you probably do not have is somebody watching
            it between incidents — testing Microsoft&rsquo;s updates against your
            customisations before they land, and spending a defined number of
            hours each month making it better rather than only making it work.
          </p>
          <Link to="/contact" className="sp-hero__cta">
            Talk through what you are dealing with
          </Link>
        </div>
      </section>

      {/* ── 2. PROBLEMS — two columns, hairlines, no numbers, no cards ──────── */}
      <section className="sp-problems">
        <div className="sp-problems__inner">
          <span className="sp-tag">Where this starts</span>
          <h2 className="sp-h2">The problems this practice solves</h2>
          <p className="sp-lede">
            Almost none of these are platform faults. They are ownership gaps —
            nobody whose job it is to watch, to test an update, or to say what is
            left to finish — and that is what this closes.
          </p>
          <ul className="sp-problems__grid">
            {PROBLEMS.map(item => (
              <li className="sp-problems__item" key={item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 3. WHAT THIS ACTUALLY IS — plain language, no bare acronyms ─────── */}
      <section className="sp-what">
        <div className="sp-what__inner">
          {/* FIX 1 — two columns. This ran 300+ words in one narrow left
              column with the right half of the page empty. The heading and a
              standfirst carry the point on the left; the argument is broken
              into its own divisions on the right, at a 34rem measure. */}
          <div className="sp-what__cols">
            <div className="sp-what__lead">
              <span className="sp-tag">In plain terms</span>
              <h2 className="sp-h2">What this actually is</h2>
              <p className="sp-what__standfirst">
                You already own the platform, so the question is no longer what to buy.
                It is whether anyone is accountable for it between incidents, and what
                arrives each month that proves they were.
              </p>
            </div>
            <div className="sp-what__body">
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">What Finance and Operations covers</h3>
              <p>
                Start with what you own. Dynamics 365 Finance and Operations — F&amp;O,
                in every document you will be sent — is the part of Microsoft&rsquo;s
                business platform that runs the money and the movement: your general
                ledger, budgeting and month-end close, your purchasing and supplier
                invoices, your stock, warehouses and transfers, your production orders
                and costing. It is the system of record for what you owe, what you hold
                and what things cost, which is precisely why a bad afternoon in it is
                not an IT problem but a trading one.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">A support contract versus a managed service</h3>
              <p>
                A support contract and a managed service are sold in similar language
                and are not the same purchase. A support contract is a queue: something
                breaks, you raise a ticket, somebody fixes that ticket, and the
                arrangement is measured by how fast tickets close. A managed service is
                accountability for the environment. The same incidents get handled, but
                so does the work that stops incidents happening — the monitoring, the
                update testing, the configuration drift nobody reported — and a defined
                number of hours goes on making the thing better rather than only
                restoring it. The honest test between them is whether anyone is paid to
                notice something when you have not complained.
              </p>
            </div>
            <div className="sp-what__part">
              <h3 className="sp-what__parttitle">What happens when Microsoft ships an update</h3>
              <p>
                Which brings up the thing that actually keeps people awake. Microsoft
                ships updates to this platform on its own schedule, and you do not get
                to decline them. If your environment has been tailored — and it has, or
                you would not be reading this — then every update meets code and
                configuration Microsoft has never seen. Handled properly, that means an
                assessment of what the update touches, a copy of your environment to try
                it on, a regression test of the things that matter, your sign-off, and a
                rollback plan you could actually execute. Handled badly, it means your
                month-end finds out first.
              </p>
            </div>
            </div>
          </div>
          <p className="sp-what__pull">
            The useful question is not who to ring when it breaks. It is who is
            accountable for it not breaking, and what you receive each month that
            proves they were.
          </p>
        </div>
      </section>

      {/* ── 4. WHAT THE SERVICE COVERS — THE DARK BAND, cards ───────────────── */}
      <section className="sp-build">
        <div className="sp-build__inner">
          <span className="sp-tag">The capabilities</span>
          <h2 className="sp-h2">What gets covered</h2>
          <p className="sp-lede">
            Six things make up the cover. Most engagements use all six, because
            leaving one out is usually where the next incident comes from.
          </p>
          <div className="sp-build__grid">
            {CAPABILITIES.map(cap => (
              <div className="sp-build__card" key={cap.title}>
                <h3 className="sp-build__cardtitle">{cap.title}</h3>
                <p className="sp-build__cardbody">{cap.body}</p>
                <ul className="sp-build__stack">
                  {cap.stack.map(tech => (
                    <li className="sp-build__stackitem" key={tech}>
                      {tech}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 5. HOW WE WORK — named phases, no week numbers, no dates ────────── */}
      <section className="sp-how">
        <div className="sp-how__inner">
          <span className="sp-tag">How the work runs</span>
          <h2 className="sp-h2">How open-ended work is run</h2>
          <p className="sp-lede">
            Cover has no delivery date, because nothing is being delivered — it
            is an arrangement that starts and then continues. What you get
            instead of a date is a clear way in, a written standard, and a clear
            way out.
          </p>
          <ol className="sp-how__steps">
            {PHASES.map(ph => (
              <li className="sp-how__step" key={ph.title}>
                <span className="sp-how__marker" aria-hidden="true" />
                <span className="sp-how__phase">{ph.phase}</span>
                <h3 className="sp-how__title">{ph.title}</h3>
                <p className="sp-how__body">{ph.body}</p>
              </li>
            ))}
          </ol>
          <p className="sp-how__note">
            No date is published for work of this shape, and none is implied. If
            what you need first is a scope and a date you can hold someone to,
            the three diagnostics below carry both.
          </p>
        </div>
      </section>

      {/* ── 6. FIXED-SCOPE OPTIONS — rows, prices shown ─────────────────────── */}
      <section className="sp-offers">
        <div className="sp-offers__inner">
          <span className="sp-tag">Fixed-scope options</span>
          <h2 className="sp-h2">Start with a diagnosis, or hand the environment over</h2>
          <p className="sp-lede">
            If you are not ready to hand over the environment, the first three
            answer the question first, and each of those has a written scope, a
            published price and a date. The last is the standing arrangement
            this page describes.
          </p>
          <ul className="sp-offers__list">
            {PRACTICE_OFFERS.map(o => (
              <li className="sp-offers__item" key={o.slug}>
                <Link to={`/offers/${o.slug}`} className="sp-offers__row">
                  <span className="sp-offers__main">
                    <span className="sp-offers__name">{o.name}</span>
                    <span className="sp-offers__problem">{o.symptoms[0]}</span>
                  </span>
                  <span className="sp-offers__meta">
                    <span className="sp-offers__timeline">
                      {o.timelineShort}
                    </span>
                    {/* GATED PRICES ARE NOT PRINTED HERE. This row used to
                        render o.priceShort unconditionally, which published the
                        figure that the offer page itself withholds behind the
                        gate — CONTENT-AUDIT §8.1. It mattered the moment a
                        gated offer joined this block: Dynamics 365 Support &
                        Continuing Development leaked its monthly fee here
                        while its own page asked for a form. The three
                        diagnostics are ungated and still show their figure.
                        NOTE: the same unguarded render still exists on the
                        other five service pages. */}
                    {!isGated(o) && (
                      <span className="sp-offers__price">{o.priceShort}</span>
                    )}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="sp-offers__all">
            <Link to="/offers">Every offer, across every practice &rarr;</Link>
          </p>
        </div>
      </section>

      {/* ── 7. PROOF — DELIBERATELY ABSENT. Neither Dynamics-titled case study
             names a client or carries a metric. See the file header. ─────── */}

      {/* ── 8. QUESTIONS — the ten kept FAQs, in the DOM ────────────────────── */}
      <section className="sp-faq">
        <div className="sp-faq__inner">
          <span className="sp-tag">Questions</span>
          <h2 className="sp-h2">What buyers ask about managed services</h2>
          <ul className="sp-faq__list">
            {FAQS.map(f => (
              <li className="sp-faq__item" key={f.q}>
                <p className="sp-faq__q">{f.q}</p>
                <p className="sp-faq__a">{f.a}</p>
                {f.bullets && (
                  <ul className="sp-faq__bullets">
                    {f.bullets.map(bi => (
                      <li className="sp-faq__bullet" key={bi}>
                        {bi}
                      </li>
                    ))}
                  </ul>
                )}
                {f.after && <p className="sp-faq__after">{f.after}</p>}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── 9. IF YOUR NEED IS BROADER — one CTA ────────────────────────────── */}
      <section className="sp-broader">
        <div className="sp-broader__inner">
          <span className="sp-tag">If your need is broader</span>
          <h2 className="sp-h2">If none of that is quite your situation</h2>
          <div className="sp-broader__cols">
            <p className="sp-broader__text">
              Plenty of what lands here does not fit a cover agreement: a
              stalled rollout that has to be finished before anything can be
              maintained, an upgrade off a version that is running out of
              support, a licence bill nobody can reconcile against actual
              adoption, or an honest second opinion on whether this platform is
              still the right one for you.
            </p>
            <p className="sp-broader__text">
              Describe what you are dealing with in a free consultancy session
              and you will get a straight read on how it would be approached,
              what it depends on, and where the risk sits. If the answer is that
              you need less than you think, that is what you will hear.
            </p>
            <p className="sp-broader__text">
              If bulk distribution is part of what your Dynamics estate has to
              handle, the{" "}
              <Link to="/caseStudies/products/mD365BulkStockDistribution">
                D365 Bulk Stock Distribution utility
              </Link>{" "}
              is one we have already built on top of Finance &amp; Operations —
              a product overview rather than a client story, but it shows the
              shape of the work.
            </p>
          </div>
          <div className="sp-broader__foot">
            <Link to="/contact" className="sp-broader__cta">
              Tell us what you are dealing with
            </Link>
          </div>
        </div>
      </section>
    </article>
  </Layout>
)

export default FinanceOperations

export const financeOperationsQuery = graphql`
  query financeOperationsQuery {
    bgImage: file(relativePath: { eq: "Mask_group8.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
